import { call, put, takeEvery } from 'redux-saga/effects';

import AuthUtil from '../../AuthUtil';
import {
    API_REQUEST,
    API_REQUEST_SUCCESS,
    API_REQUEST_FAILURE,
} from '../../constants/api';
import { showErrorMessage } from '../../actions/global';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../components/dashboard/constants';

/**
 * Generic API request saga.
 *
 * Routes the call through AuthUtil so:
 *   - Bearer token is attached automatically
 *   - 401/403 responses bounce the user to /login (handled inside AuthUtil)
 *
 * On success: dispatches API_REQUEST_SUCCESS with the raw response.data
 *             and invokes the optional onSuccess callback.
 * On failure: dispatches API_REQUEST_FAILURE, surfaces the error via the
 *             global ErrorFeedbackBar (unless `silent: true`), and invokes
 *             the optional onError callback.
 */
function* apiRequestSaga(action) {
    const {
        key,
        endpoint,
        method,
        data,
        onSuccess,
        onError,
        onProgress,
        silent,
    } = action;

    try {
        let response;
        const verb = (method || 'POST').toUpperCase();

        // Upload-with-progress always uses postUploadRequest, even if the
        // verb is technically POST, so axios's onUploadProgress fires.
        if (typeof onProgress === 'function' && verb === 'POST') {
            response = yield call(() =>
                AuthUtil.postUploadRequest(endpoint, data, onProgress),
            );
        } else {
            switch (verb) {
                case 'GET':
                    response = yield call(() => AuthUtil.getRequest(endpoint));
                    break;
                case 'PUT':
                    response = yield call(() =>
                        AuthUtil.putRequest(endpoint, data),
                    );
                    break;
                case 'POST':
                default:
                    response = yield call(() =>
                        AuthUtil.postRequest(endpoint, data),
                    );
                    break;
            }
        }

        const payload = response?.data;

        // The saga always treats an HTTP 200 as success and forwards the
        // raw payload to onSuccess. Some BeeNexa endpoints return
        // { success: false } for non-error states (e.g. "no saved week
        // data for this period yet"), so the component decides what to do
        // with the success flag — the saga must not pre-route to onError.
        yield put({ type: API_REQUEST_SUCCESS, key, data: payload });
        if (typeof onSuccess === 'function') onSuccess(payload);
    } catch (error) {
        const message =
            error?.response?.data?.message ||
            error?.message ||
            SERVER_FAILED_ERROR_MESSAGE;
        yield put({ type: API_REQUEST_FAILURE, key, error: message });
        if (!silent) yield put(showErrorMessage(message));
        if (typeof onError === 'function') onError(message);
    }
}

export default function* apiSagas() {
    // takeEvery so multiple parallel requests can run for different keys.
    yield takeEvery(API_REQUEST, apiRequestSaga);
}
